import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const GA_MEASUREMENT_ID = 'G-5DE8QCH4PG';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    TENANT_ID: string | null;
  }
}

interface GoogleAnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that initializes GA4 with tenant_id and tracks SPA navigation.
 * Wrap your app with this component inside BrowserRouter.
 */
export function GoogleAnalyticsProvider({ children }: GoogleAnalyticsProviderProps) {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState<string | null>(null);

  // Resolve tenant_id from hostname
  useEffect(() => {
    resolveTenantId();
  }, []);

  const resolveTenantId = async () => {
    try {
      const hostname = window.location.hostname;
      
      const isDevelopment = 
        hostname === 'localhost' || 
        hostname.includes('lovable.app') ||
        hostname.includes('127.0.0.1');

      let resolvedTenantId: string | null = null;

      if (!isDevelopment) {
        // Production: Match by primary_domain
        const hostWithoutWww = hostname.replace(/^www\./, '');
        const hostWithWww = hostname.startsWith('www.') ? hostname : `www.${hostname}`;

        const { data: domainMatch } = await supabase
          .from('sites_public' as any)
          .select('tenant_id')
          .eq('status', 'active')
          .or(`primary_domain.eq.${hostname},primary_domain.eq.${hostWithoutWww},primary_domain.eq.${hostWithWww}`)
          .limit(1)
          .maybeSingle();

        if ((domainMatch as any)?.tenant_id) {
          resolvedTenantId = (domainMatch as any).tenant_id;
        }
      }

      // Fallback: First active site
      if (!resolvedTenantId) {
        const { data: fallbackSite } = await supabase
          .from('sites_public' as any)
          .select('tenant_id')
          .eq('status', 'active')
          .limit(1)
          .maybeSingle();

        resolvedTenantId = (fallbackSite as any)?.tenant_id || null;
      }

      if (resolvedTenantId) {
        setTenantId(resolvedTenantId);
        window.TENANT_ID = resolvedTenantId;
      }
    } catch (err) {
      console.error('[GA] Error resolving tenant_id:', err);
    }
  };

  // Configure GA4 with tenant_id
  useEffect(() => {
    if (!tenantId || isConfigured) return;

    // Configure GA4 with tenant_id - fires initial page_view
    if (typeof window.gtag !== 'function') return;
    window.gtag('config', GA_MEASUREMENT_ID, {
      tenant_id: tenantId,
      send_page_view: true
    });

    setIsConfigured(true);
    setPreviousPath(location.pathname);

    console.log('[GA] Configured with tenant_id:', tenantId);
  }, [tenantId, isConfigured, location.pathname]);

  // Track SPA route changes
  useEffect(() => {
    if (!isConfigured || !tenantId) return;
    
    // Skip initial load (already tracked by config)
    if (previousPath === null) {
      setPreviousPath(location.pathname);
      return;
    }

    // Skip if path hasn't changed
    if (previousPath === location.pathname) return;

    // Fire page_view for SPA navigation
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname,
      page_location: window.location.href,
      page_title: document.title,
      tenant_id: tenantId
    });

    setPreviousPath(location.pathname);
  }, [location.pathname, tenantId, isConfigured, previousPath]);

  return <>{children}</>;
}
